import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { Plug, X, Zap, Search, Check } from 'lucide-react'
import { toast } from 'sonner'

// ─── Integration Catalog ──────────────────────────────────────────────────────
// Pre-launch posture: we capture customer interest, not credentials. The
// `fields` arrays from the old credential-capture flow were removed Session 30
// when this surface was rebuilt — see migration 20260511091512_integration_requests.sql.
const INTEGRATIONS = [
  {
    category: 'Payments',
    items: [
      { key: 'stripe',     name: 'Stripe',     description: 'Accept payments, send payment links on invoices, set up recurring billing.', icon: '💳', color: '#635bff' },
      { key: 'square',     name: 'Square',     description: 'Accept in-person and online payments through Square.',                       icon: '⬛', color: '#006aff' },
      { key: 'quickbooks', name: 'QuickBooks', description: 'Sync invoices, expenses, and customers with QuickBooks Online.',             icon: '📊', color: '#2ca01c' },
    ],
  },
  {
    category: 'Communication',
    items: [
      { key: 'twilio',   name: 'Twilio',   description: 'Send SMS notifications, review requests, and job updates to customers.', icon: '📱', color: '#f22f46' },
      { key: 'sendgrid', name: 'SendGrid', description: 'Send transactional emails for invoices, review requests, and notifications.', icon: '✉️', color: '#1a82e2' },
      { key: 'slack',    name: 'Slack',    description: 'Get real-time notifications for new jobs, reviews, and conversations in Slack.', icon: '💬', color: '#4a154b' },
    ],
  },
  {
    category: 'Marketing & CRM',
    items: [
      { key: 'mailchimp',        name: 'Mailchimp',        description: 'Sync contacts and automate email marketing campaigns.',     icon: '🐵', color: '#ffe01b' },
      { key: 'hubspot',          name: 'HubSpot',          description: 'Sync contacts, deals, and activity with HubSpot CRM.',       icon: '🟠', color: '#ff7a59' },
      { key: 'google_analytics', name: 'Google Analytics', description: 'Track customer portal visits and conversion events.',         icon: '📈', color: '#f9ab00' },
    ],
  },
  {
    category: 'Automation',
    items: [
      { key: 'zapier', name: 'Zapier',          description: 'Connect to 5,000+ apps. Trigger zaps from jobs, invoices, reviews, and more.', icon: '⚡', color: '#ff4a00' },
      { key: 'make',   name: 'Make (Integromat)', description: 'Build powerful automation scenarios with visual workflow builder.',        icon: '🔮', color: '#6d00cc' },
      { key: 'n8n',    name: 'n8n',             description: 'Self-hosted automation platform for advanced workflow automation.',         icon: '🔁', color: '#ea4b71' },
    ],
  },
  {
    category: 'Reviews & Reputation',
    items: [
      { key: 'google_business', name: 'Google Business', description: 'Pull in Google reviews automatically and respond from within Orkestra.', icon: '🔵', color: '#4285f4' },
      { key: 'trustpilot',      name: 'Trustpilot',      description: 'Sync Trustpilot reviews and send automated review invitations.',         icon: '🟢', color: '#00b67a' },
    ],
  },
  {
    category: 'Field & Scheduling',
    items: [
      { key: 'google_calendar', name: 'Google Calendar', description: 'Sync field jobs with Google Calendar. Two-way sync with your team.',     icon: '📅', color: '#4285f4' },
      { key: 'google_maps',     name: 'Google Maps',     description: 'Enable live map view in Field Hub and route optimization for technicians.', icon: '🗺️', color: '#34a853' },
    ],
  },
]

// ─── Request Access Modal ─────────────────────────────────────────────────────
function RequestAccessModal({ integration, onClose, onSaved }: any) {
  const { workspace, user } = useAuth()
  const [useCase, setUseCase] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    const { error } = await supabase.from('integration_requests').insert({
      workspace_id: workspace!.id,
      integration_key: integration.key,
      integration_name: integration.name,
      use_case: useCase.trim() || null,
      requested_by: user?.id || null,
      status: 'requested',
    })
    setSaving(false)
    if (error) { toast.error(`Error: ${error.message}`); return }
    toast.success("Request received — we'll be in touch")
    onSaved()
    onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 22, padding: 28, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
          {/* Integration brand chip — integration.color is fixed brand hex, intentionally non-theming */}
          <div style={{ width: 48, height: 48, borderRadius: 14, background: `${integration.color}18`, border: `1px solid ${integration.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
            {integration.icon}
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>Request priority access</h2>
            <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>{integration.name}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '14px 16px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 12, marginBottom: 18 }}>
          <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.55 }}>
            We're prioritizing integrations based on customer interest. Tell us how you'd use {integration.name} and we'll reach out when it's ready.
          </p>
        </div>

        <div style={{ marginBottom: 22 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Your use case (optional)</label>
          <textarea value={useCase} onChange={e => setUseCase(e.target.value)}
            placeholder="How would you use this integration? What workflows would it support?"
            rows={4}
            style={{ width: '100%', padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box', resize: 'none', lineHeight: 1.5 }}
            onFocus={e => e.currentTarget.style.borderColor = `${integration.color}60`}
            onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
          />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 2, justifyContent: 'center' }}>
            {saving ? 'Submitting...' : 'Request access'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

// ─── Integration Card ─────────────────────────────────────────────────────────
function IntegrationCard({ integration, isRequested, onClick }: any) {
  return (
    <motion.div whileHover={{ y: -2 }} onClick={onClick}
      style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '18px 20px', cursor: 'pointer', transition: 'border-color 200ms ease', position: 'relative', overflow: 'hidden' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = `${integration.color}25` }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)' }}>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
        {/* Integration brand chip — fixed brand hex, intentionally non-theming */}
        <div style={{ width: 42, height: 42, borderRadius: 12, background: `${integration.color}15`, border: `1px solid ${integration.color}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
          {integration.icon}
        </div>
        {isRequested ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 100 }}>
            <Check style={{ width: 11, height: 11, color: 'var(--accent-text)' }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent-text)' }}>Requested</span>
          </div>
        ) : (
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', padding: '4px 10px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 100 }}>Coming soon</span>
        )}
      </div>

      <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 5px' }}>{integration.name}</h3>
      <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '0 0 14px', lineHeight: 1.5 }}>{integration.description}</p>

      <button style={{ width: '100%', padding: '8px', background: isRequested ? 'var(--accent-dim)' : 'var(--hover-bg)', border: `1px solid ${isRequested ? 'var(--accent-border)' : 'var(--border-default)'}`, borderRadius: 9, color: isRequested ? 'var(--accent-text)' : 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 150ms ease' }}>
        {isRequested ? 'Request again' : 'Request access'}
      </button>
    </motion.div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function IntegrationsHub() {
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const [selectedIntegration, setSelectedIntegration] = useState<any>(null)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')

  const { data: requests = [] } = useQuery({
    queryKey: ['integration-requests', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('integration_requests').select('integration_key, status').eq('workspace_id', workspace.id)
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const requestedKeys = new Set(requests.map((r: any) => r.integration_key))

  const allCategories = INTEGRATIONS.map(c => c.category)

  const filteredIntegrations = INTEGRATIONS
    .map(cat => ({
      ...cat,
      items: cat.items.filter(item => {
        const matchesSearch = !search || item.name.toLowerCase().includes(search.toLowerCase()) || item.description.toLowerCase().includes(search.toLowerCase())
        const matchesCategory = categoryFilter === 'all' || cat.category === categoryFilter
        return matchesSearch && matchesCategory
      }),
    }))
    .filter(cat => cat.items.length > 0)

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>
      {/* Header */}
      <div style={{ padding: '24px 28px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>Integrations</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>
              {INTEGRATIONS.reduce((s, c) => s + c.items.length, 0)} integrations in the roadmap · request priority access to shape what ships first
            </p>
          </div>
        </div>

        {/* Pre-launch banner */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 12, marginBottom: 20 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'color-mix(in srgb, var(--accent) var(--status-tint-fill), transparent)', border: '1px solid var(--accent-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Plug style={{ width: 16, height: 16, color: 'var(--accent-text)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 3px' }}>Integrations are in active development</p>
            <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0 }}>Tell us which ones you'd use and we'll prioritize what ships first. We'll reach out when your requested integrations are ready.</p>
          </div>
        </div>

        {/* Search + filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
            <Search style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search integrations..."
              style={{ width: '100%', padding: '9px 12px 9px 32px', background: 'var(--field-bg)', border: '1px solid var(--border-subtle)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'} />
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button onClick={() => setCategoryFilter('all')}
              style={{ padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: categoryFilter === 'all' ? 'var(--accent)' : 'var(--hover-bg)', color: categoryFilter === 'all' ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: categoryFilter === 'all' ? 'none' : '1px solid var(--border-subtle)' }}>
              All
            </button>
            {allCategories.map(cat => (
              <button key={cat} onClick={() => setCategoryFilter(cat)}
                style={{ padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: categoryFilter === cat ? 'var(--accent)' : 'var(--hover-bg)', color: categoryFilter === cat ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: categoryFilter === cat ? 'none' : '1px solid var(--border-subtle)' }}>
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Integration grid */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 28px 28px' }}>
        {filteredIntegrations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px' }}>
            <Plug style={{ width: 36, height: 36, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>No integrations found</p>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Try a different search term</p>
          </div>
        ) : (
          filteredIntegrations.map(cat => (
            <div key={cat.category} style={{ marginBottom: 32 }}>
              <h2 style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 14px' }}>
                {cat.category}
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
                {cat.items.map(item => (
                  <IntegrationCard
                    key={item.key}
                    integration={item}
                    isRequested={requestedKeys.has(item.key)}
                    onClick={() => setSelectedIntegration(item)}
                  />
                ))}
              </div>
            </div>
          ))
        )}

        {/* Zapier CTA */}
        <div style={{ background: 'linear-gradient(135deg, rgba(255,74,0,0.08), rgba(124,58,237,0.08))', border: '1px solid rgba(255,74,0,0.2)', borderRadius: 16, padding: '24px 28px', display: 'flex', alignItems: 'center', gap: 20, marginTop: 8 }}>
          <div style={{ width: 52, height: 52, borderRadius: 16, background: 'rgba(255,74,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0 }}>⚡</div>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 5px', letterSpacing: '-0.01em' }}>Want to connect 5,000+ apps with Zapier?</h3>
            <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.5 }}>
              Zapier support is on the roadmap. Request priority access and tell us which apps you need it for — we'll prioritize the most-asked-for paths.
            </p>
          </div>
          <button onClick={() => setSelectedIntegration(INTEGRATIONS[3].items[0])}
            className="btn-primary" style={{ flexShrink: 0, fontSize: 13 }}>
            <Zap style={{ width: 14, height: 14 }} /> Request Zapier
          </button>
        </div>
      </div>

      {/* Request Access Modal */}
      <AnimatePresence>
        {selectedIntegration && (
          <RequestAccessModal
            integration={selectedIntegration}
            onClose={() => setSelectedIntegration(null)}
            onSaved={() => queryClient.invalidateQueries({ queryKey: ['integration-requests'] })}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
